// How streams, codecs and containers are written for people. Codec and container names are
// proper names - "HEVC", "DTS-HD MA" - and read the same in every language, so they are not
// translated; the words around them are, by whoever calls these.

import type { MediaStreamInfo } from '../../api/types';

/// Containers write "und" when nobody set a language. Showing it is worse than showing
/// nothing: it looks like a language and is not one.
export function language(tag: string | null | undefined): string | undefined {
  return tag && tag.toLowerCase() !== 'und' && tag.toLowerCase() !== 'unknown'
    ? tag.toUpperCase()
    : undefined;
}

/// A rate as it is spoken: megabits once there are any, and no decimal where it is a round
/// number - "20 Mbit/s" is a choice somebody made, "20.0 Mbit/s" is a measurement.
export function formatBitrate(bitsPerSecond: number): string {
  return bitsPerSecond >= 1_000_000
    ? `${(bitsPerSecond / 1_000_000).toFixed(bitsPerSecond % 1_000_000 === 0 ? 0 : 1)} Mbit/s`
    : `${Math.round(bitsPerSecond / 1_000)} kbit/s`;
}

/// A channel count as it is printed on a box: 5.1 rather than 6.
export function channelLayout(channels: number): string {
  switch (channels) {
    case 1:
      return '1.0';
    case 2:
      return '2.0';
    case 3:
      return '2.1';
    case 6:
      return '5.1';
    case 7:
      return '6.1';
    case 8:
      return '7.1';
    default:
      return `${channels}.0`;
  }
}

/// libav* codec names as the formats are known outside of libav*.
const CODEC_NAMES: Record<string, string> = {
  h264: 'H.264',
  hevc: 'HEVC',
  av1: 'AV1',
  vp8: 'VP8',
  vp9: 'VP9',
  mpeg2video: 'MPEG-2',
  mpeg4: 'MPEG-4',
  vc1: 'VC-1',
  aac: 'AAC',
  ac3: 'AC-3',
  eac3: 'E-AC-3',
  dts: 'DTS',
  truehd: 'TrueHD',
  flac: 'FLAC',
  opus: 'Opus',
  vorbis: 'Vorbis',
  mp2: 'MP2',
  mp3: 'MP3',
  pcm_s16le: 'PCM',
  pcm_s24le: 'PCM',
  alac: 'ALAC',
  subrip: 'SRT',
  ass: 'ASS',
  ssa: 'SSA',
  webvtt: 'WebVTT',
  mov_text: 'TX3G',
  hdmv_pgs_subtitle: 'PGS',
  dvd_subtitle: 'VobSub',
  dvb_subtitle: 'DVB',
  dvb_teletext: 'Teletext',
};

export function codecName(codec: string): string {
  return CODEC_NAMES[codec.toLowerCase()] ?? codec.toUpperCase();
}

const CONTAINER_NAMES: Record<string, string> = {
  'matroska,webm': 'MKV',
  matroska: 'MKV',
  webm: 'WebM',
  'mov,mp4,m4a,3gp,3g2,mj2': 'MP4',
  mp4: 'MP4',
  mov: 'MOV',
  mpegts: 'MPEG-TS',
  hls: 'HLS',
  avi: 'AVI',
  flv: 'FLV',
};

export function containerName(container: string): string {
  return CONTAINER_NAMES[container.toLowerCase()] ?? container.split(',')[0].toUpperCase();
}

const RANGE_NAMES: Record<string, string | undefined> = {
  Sdr: undefined,
  Hdr10: 'HDR10',
  Hdr10Plus: 'HDR10+',
  HybridLogGamma: 'HLG',
  DolbyVision: 'Dolby Vision',
};

export function rangeName(range: string | undefined): string | undefined {
  // SDR is named by leaving it out; only a range that is not in the table is shown as it came.
  return range && !(range in RANGE_NAMES) ? range : RANGE_NAMES[range ?? ''];
}

/// A video stream in one line: codec and profile, size, range, depth and rate.
export function describeVideo(stream: MediaStreamInfo): string {
  const profile = stream.profile ?? undefined;
  const parts = [[codecName(stream.codec), profile].filter(Boolean).join(' ')];

  if (stream.width && stream.height) {
    parts.push(`${stream.width}×${stream.height}`);
  }

  // Dolby Vision is named with its profile: 5 and 8 are shown very differently by a player,
  // and "Dolby Vision" alone does not say which of them a file is.
  const range = stream.dolbyVision
    ? `Dolby Vision ${stream.dolbyVision.profile}`
    : rangeName(stream.range);

  if (range) {
    parts.push(range);
  }

  // Main 10 says it already; anything else deeper than eight bits is worth saying.
  if (stream.bitDepth && stream.bitDepth > 8 && !profile?.includes('10')) {
    parts.push(`${stream.bitDepth}-bit`);
  }

  if (stream.frameRate) {
    parts.push(`${Number(stream.frameRate.toFixed(3))} fps`);
  }

  return parts.join(' · ');
}

/// An audio stream in one line: the format as it is sold - "DTS-HD MA" rather than "dts" - its
/// channels and its language.
export function describeAudio(stream: MediaStreamInfo): string {
  const codec = stream.codec.toLowerCase();
  const format = codec === 'dts' && stream.profile
    ? stream.profile
    : codec === 'aac' && stream.profile && /he/i.test(stream.profile)
      ? stream.profile
      : codecName(stream.codec);

  return [format, stream.channels ? channelLayout(stream.channels) : undefined, language(stream.language)]
    .filter(Boolean)
    .join(' · ');
}

/// A subtitle stream in one line: its format, its language and its own title.
export function describeSubtitle(stream: MediaStreamInfo): string {
  return [codecName(stream.codec), language(stream.language), stream.title].filter(Boolean).join(' · ');
}

/// Any stream, as its kind is written.
export function describeStream(stream: MediaStreamInfo): string {
  switch (stream.kind) {
    case 'Video':
      return describeVideo(stream);
    case 'Audio':
      return describeAudio(stream);
    default:
      return describeSubtitle(stream);
  }
}

/// A value out of a decision reason, written the way the rest of the panel writes it: "HEVC"
/// rather than "hevc", "HDR10" rather than "Hdr10". A list stays a list. Anything the tables do
/// not know - a container, a filter name, "10 bit" - is left exactly as the server said it,
/// because guessing at it reads worse than the original.
export function prettyValue(value: string): string {
  return value
    .split(/,\s*/)
    .map((token) => {
      const key = token.trim();

      if (key.toLowerCase() in CODEC_NAMES) {
        return codecName(key);
      }

      // SDR has no entry of its own, because a stream line says it by leaving it out. As a
      // value in a sentence it is spelt the way the others are.
      return key in RANGE_NAMES ? RANGE_NAMES[key] ?? key.toUpperCase() : key;
    })
    .join(', ');
}

/// A playing time as a clock shows it: 1:02:03, or 2:03 under an hour.
export function formatClock(totalSeconds: number): string {
  const seconds = Math.max(Math.floor(totalSeconds), 0);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = String(seconds % 60).padStart(2, '0');

  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${rest}` : `${minutes}:${rest}`;
}
